/**
 * Unified push notification service
 * Automatically uses native Capacitor push on mobile apps,
 * and Firebase Web push on browsers.
 */
import { Capacitor } from '@capacitor/core';

// Dynamic native detection - checks both at load time and runtime
// This handles the case where the Capacitor bridge is injected after JS bundle loads
const checkIsNative = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

// Check if web Notification API is available (not available in Android WebView)
const hasWebNotifications = () => typeof Notification !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window;

// Safely load firebase module only when Notification API exists
const getFirebase = () => {
  if (!hasWebNotifications()) return null;
  try {
    return require('../firebase');
  } catch (e) {
    console.error('Firebase load error:', e);
    return null;
  }
};

export const requestNotificationPermission = async () => {
  if (checkIsNative()) {
    const { requestNativeNotificationPermission } = await import('./nativePush');
    return requestNativeNotificationPermission();
  }
  if (!hasWebNotifications()) return null;
  const { requestNotificationPermission: webRequest } = await import('../firebase');
  return webRequest();
};

export const onForegroundMessage = (callback) => {
  if (checkIsNative()) {
    const setup = async () => {
      const { setupNativeNotificationListeners } = await import('./nativePush');
      return setupNativeNotificationListeners((notification) => {
        callback({
          data: notification.data || {},
          notification: { title: notification.title, body: notification.body }
        });
      });
    };
    let cleanup = () => {};
    setup().then(fn => { cleanup = fn; });
    return () => cleanup();
  }
  const firebase = getFirebase();
  if (!firebase) return () => {};
  return firebase.onForegroundMessage(callback);
};

export const areNotificationsEnabled = () => {
  if (checkIsNative()) return true;
  const firebase = getFirebase();
  if (!firebase) return false;
  return firebase.areNotificationsEnabled();
};

export const isNotificationSupported = () => {
  if (checkIsNative()) return true;
  return hasWebNotifications();
};

export const isNativePlatform = () => checkIsNative();

export const getPushPlatform = () => {
  try {
    return Capacitor.getPlatform(); // 'web', 'android', 'ios'
  } catch {
    return 'web';
  }
};

// 'granted' | 'denied' | 'prompt' | 'unsupported' — never triggers the OS prompt.
export const getPushPermissionState = async () => {
  if (checkIsNative()) {
    const { checkNativeNotificationPermission } = await import('./nativePush');
    const s = await checkNativeNotificationPermission();
    if (s === 'granted' || s === 'denied') return s;
    return s ? 'prompt' : 'unsupported';
  }
  if (!hasWebNotifications()) return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  return 'prompt';
};

// Fetch + return a push token ONLY if permission is already granted (no OS prompt).
export const getPushTokenIfGranted = async () => {
  if ((await getPushPermissionState()) !== 'granted') return null;
  if (checkIsNative()) {
    const { fetchNativeTokenSilently } = await import('./nativePush');
    return fetchNativeTokenSilently();
  }
  const { requestNotificationPermission: webRequest } = await import('../firebase');
  return webRequest();
};
